/**
 * RIG pure logic — basis mapping and small derivations. Kept dependency-free and
 * side-effect-free so it is exercised by node-env vitest (the harness has no
 * jsdom/component testing).
 *
 * Sealed law encoded here: the five identity bases collapse to DIRECT;
 * `participant` stands alone; excluded/none never surface. Density
 * (RIG-DENSITY-1) ranks by strength → temperature → recency and caps to the top
 * few per org — strength ranks visibility, it never hides (see the adapter).
 */

import type { BasisFamily, Temperature, TextSegment } from "./types";

/** Mirrors the server's ResolutionBasis union (organization-resolver.ts). Kept
 *  local so frontend logic is testable without importing edge-function code. */
export type MatchBasis =
  | "explicit_organization_id"
  | "project_id"
  | "project_id_redirect"
  | "exact_canonical_name"
  | "exact_ratified_alias"
  | "participant"
  | "excluded_project_id"
  | "excluded_internal_label"
  | "none";

export type BasisClass = BasisFamily | "excluded";

const DIRECT_BASES = new Set<MatchBasis>([
  "explicit_organization_id",
  "project_id",
  "project_id_redirect",
  "exact_canonical_name",
  "exact_ratified_alias",
]);

/** Collapse a raw match basis to its surface family. Unknown strings are
 *  treated as excluded (fail closed — never render an unrecognized basis). */
export function basisClass(basis: string): BasisClass {
  if (basis === "participant") return "participant";
  if (DIRECT_BASES.has(basis as MatchBasis)) return "direct";
  return "excluded";
}

/** Whole days between an ISO/date string and a reference instant. Null when the
 *  input is missing or unparseable. Never negative (future dates clamp to 0). */
export function daysAgo(iso: string | null, nowMs: number): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const diff = Math.floor((nowMs - then) / 86_400_000);
  return diff < 0 ? 0 : diff;
}

/** Up-to-two initials from a display name, for the graph node badge. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");
}

const TEMPS: ReadonlySet<Temperature> = new Set<Temperature>([
  "hot",
  "warm",
  "cooling",
  "cold",
]);

/** Normalize a free-form temperature string to the sealed vocabulary; unknown
 *  or missing values fall to "cold" (the least-committal pip). */
export function normalizeTemperature(temp: string | null): Temperature {
  const t = (temp ?? "").toLowerCase();
  return TEMPS.has(t as Temperature) ? (t as Temperature) : "cold";
}

export const TEMP_LABEL: Record<Temperature, string> = {
  hot: "Hot",
  warm: "Warm",
  cooling: "Cooling",
  cold: "Cold",
};

/** Owner initials for the graph badge; the unassigned marker "·" when null. */
export function ownerInitial(owner: string | null): string {
  if (!owner) return "·";
  return initials(owner) || "·";
}

/** Compose a "N · PARTICIPANT" style basis-mix summary for a cluster. */
export function basisMix(count: number, basis: BasisFamily): string {
  return `${count} · ${basis === "participant" ? "PARTICIPANT" : "DIRECT"}`;
}

/** Compose a temperature band like "1 WARM · 3 COLD" from a temperature list,
 *  omitting empty bands and ordered hot→cold. */
export function temperatureBand(temps: Temperature[]): string {
  const order: Temperature[] = ["hot", "warm", "cooling", "cold"];
  const counts = new Map<Temperature, number>();
  for (const t of temps) counts.set(t, (counts.get(t) ?? 0) + 1);
  const parts = order
    .filter((t) => (counts.get(t) ?? 0) > 0)
    .map((t) => `${counts.get(t)} ${t.toUpperCase()}`);
  return parts.join(" · ");
}

/** Build the sealed per-contact relationship phrase. `signalCompany` is the
 *  resolved signal subject; `waypointName` is the consortium (participant
 *  ties). Templated generically when specifics are unknown. */
export function relationshipPhrase(
  basis: BasisFamily,
  signalCompany: string | null,
  waypointName: string | null,
): string {
  if (basis === "direct") {
    return signalCompany ? `Directly at ${signalCompany}.` : "Direct relationship.";
  }
  const via = waypointName ? `the ${waypointName}` : "the consortium";
  const not = signalCompany ? ` — not a ${signalCompany} employee.` : ".";
  return `Participates in ${via}${not}`;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Format an ISO/date string as "12 Oct 2025" (deterministic, locale-free so
 *  tests are stable across timezones). Returns "" for null, or the input
 *  unchanged if it is not a leading YYYY-MM-DD. */
export function formatDayMonYear(iso: string | null): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const mon = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${Number(m[3])} ${mon} ${m[1]}`;
}

/** Compact uppercase practice label for the graph hex: initials for multi-word
 *  practices ("Transaction Excellence" → "TE"), a truncated head for one-word
 *  ("Restructuring" → "RESTRUCT."). */
export function abbreviatePractice(practice: string): string {
  const words = practice.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words.map((w) => w[0]?.toUpperCase() ?? "").join("").slice(0, 4);
  }
  const up = (words[0] ?? practice).toUpperCase();
  return up.length > 8 ? `${up.slice(0, 8)}.` : up;
}

/** " — Title, Company" role suffix, omitting whichever parts are absent so a
 *  missing title or (v6-unsourceable) participant employer renders honestly
 *  instead of a dangling comma. Returns "" when both are absent. */
export function roleSuffix(title: string | null, company: string | null): string {
  const parts = [title, company].filter((p): p is string => Boolean(p));
  return parts.length ? ` — ${parts.join(", ")}` : "";
}

/** Split "N direct · M participant · +K clustered · Posture — tail" into
 *  segments so the posture phrase renders bold, matching the sealed entry
 *  summary, without dangerouslySetInnerHTML. */
export function entrySummary(
  direct: number,
  participant: number,
  clustered: number,
  posture: string,
): TextSegment[] {
  const counts = `${direct} direct · ${participant} participant · +${clustered} clustered · `;
  return [
    { text: counts },
    { text: posture, bold: true },
    { text: " — one relationship story, one signal." },
  ];
}
